import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { MapComponent } from "@/components/MapComponent";
import { getRootStore } from "@/stores/RootStore";
import styles from "@/styles/OrderDetail/TrackingMiniMap.module.less";
const { Text } = Typography;
const LOCATOR_ID_TECH = "order-detail-tech-locator";
const LOCATOR_ID_DEST = "order-detail-dest-locator";
/**
 * Mini-map for the order detail page showing the technician's current position
 * and the destination (client address). Also starts TechnicianLocationObserver
 * for higher-frequency location updates when a technicianId is available.
 *
 * Cleanup on unmount:
 *  - Stops TechnicianLocationObserver
 *  - Clears all map locators (mapStore.clearAllLocators)
 */
const TrackingMiniMap = observer(({ techLatitude, techLongitude, destLatitude, destLongitude, technicianId, routePoints }) => {
    const { mapStore, technicianLocationObserver } = getRootStore();
    // Track whether we started the observer so we can stop it on cleanup
    const observerStartedRef = useRef(false);
    // Derive ETA: find the first route point that has expectedArrivalTime
    const etaString = (() => {
        const point = routePoints.find((rp) => rp.expectedArrivalTime !== null);
        if (!point || !point.expectedArrivalTime)
            return null;
        try {
            return new Date(point.expectedArrivalTime).toLocaleTimeString("ru-RU", {
                hour: "2-digit",
                minute: "2-digit",
            });
        }
        catch {
            return null;
        }
    })();
    // Add/update tech and destination locators when map is ready or coords change
    useEffect(() => {
        if (!mapStore.isInitialized)
            return;
        // Technician locator
        if (techLatitude !== null && techLongitude !== null) {
            const techLocator = {
                id: LOCATOR_ID_TECH,
                position: {
                    latitude: techLatitude,
                    longitude: techLongitude,
                    address: `Техник: ${techLatitude.toFixed(4)}, ${techLongitude.toFixed(4)}`,
                },
                title: "Техник",
                icon: "🧑‍🔧",
                isVisible: true,
            };
            if (mapStore.getLocatorById(LOCATOR_ID_TECH)) {
                mapStore.updateLocator(LOCATOR_ID_TECH, techLocator);
            }
            else {
                mapStore.addLocator(techLocator);
            }
        }
        // Destination locator
        if (destLatitude !== null && destLongitude !== null) {
            const destLocator = {
                id: LOCATOR_ID_DEST,
                position: {
                    latitude: destLatitude,
                    longitude: destLongitude,
                    address: `Пункт назначения`,
                },
                title: "Точка зарядки",
                icon: "⚡",
                isVisible: true,
            };
            if (mapStore.getLocatorById(LOCATOR_ID_DEST)) {
                mapStore.updateLocator(LOCATOR_ID_DEST, destLocator);
            }
            else {
                mapStore.addLocator(destLocator);
            }
        }
        // Fit both points in view
        const points = [];
        if (techLatitude !== null && techLongitude !== null) {
            points.push({ latitude: techLatitude, longitude: techLongitude, address: "" });
        }
        if (destLatitude !== null && destLongitude !== null) {
            points.push({ latitude: destLatitude, longitude: destLongitude, address: "" });
        }
        if (points.length > 0) {
            setTimeout(() => mapStore.fitBounds(points), 300);
        }
    }, [
        mapStore.isInitialized,
        techLatitude,
        techLongitude,
        destLatitude,
        destLongitude,
    ]);
    // Start TechnicianLocationObserver for higher-frequency live updates
    useEffect(() => {
        if (!technicianId)
            return;
        technicianLocationObserver.startObserving(technicianId).then(() => {
            observerStartedRef.current = true;
        }).catch((err) => {
            console.warn("TrackingMiniMap: could not start TechnicianLocationObserver", err);
        });
        return () => {
            if (observerStartedRef.current) {
                technicianLocationObserver.stopObserving();
                observerStartedRef.current = false;
            }
        };
    }, [technicianId]);
    // Cleanup locators on unmount
    useEffect(() => {
        return () => {
            mapStore.clearAllLocators();
        };
    }, []);
    return (_jsxs("div", { className: styles.wrapper, children: [_jsxs("div", { className: styles.header, children: [_jsx(Text, { strong: true, className: styles.title, children: "\u041E\u0442\u0441\u043B\u0435\u0436\u0438\u0432\u0430\u043D\u0438\u0435 \u0442\u0435\u0445\u043D\u0438\u043A\u0430" }), etaString !== null && (_jsxs(Text, { type: "secondary", className: styles.eta, children: ["\u041E\u0436\u0438\u0434\u0430\u0435\u043C\u043E\u0435 \u043F\u0440\u0438\u0431\u044B\u0442\u0438\u0435: ", _jsx(Text, { strong: true, children: etaString })] }))] }), _jsx(MapComponent, { width: "100%", height: "300px", showCard: false, showLoadingSpinner: true, showErrorAlert: false, showInfo: false, config: {
                    center: {
                        address: "Москва, Россия",
                        latitude: techLatitude ?? 55.7558,
                        longitude: techLongitude ?? 37.6176,
                    },
                    zoom: 13,
                } })] }));
});
TrackingMiniMap.displayName = "TrackingMiniMap";
export default TrackingMiniMap;
